import axiosClient from './axios';

// ============ Types ============

export interface Expert {
  user_id: number;
  name: string;
  headline?: string;
  bio: string;
  image: string | null;
  is_verified: boolean;
  is_expert: boolean;
  sebi_qualification: string;
  sebi_reg_number: string;
  user_avg_rating: number;
  languages: string[];
  years_of_xp: number;
  call_session_rate: number;
  chat_session_rate: number;
  company_associated: boolean;
  company_name: string;
  specializations: string[];
  next_available: string | null;
  hours_until_available: number | null;
  match_score: number;
  match_reasons: string[];
  total_ratings_count: number;
  total_completed_sessions: number;
  calls_accuracy_pct?: number;
  total_calls_made?: number;
  calls_avg_return?: number;
  has_track_record?: boolean;
}

// ============ Stock Call Types ============

export interface StockCall {
  id: number;
  ticker_symbol: string;
  company_name: string;
  exchange: string;
  call_type: 'BUY' | 'SELL';
  entry_price: string;
  target_price: string;
  stop_loss_price: string;
  time_horizon_days: number;
  rationale: string | null;
  status: 'OPEN' | 'TARGET_HIT' | 'SL_HIT' | 'EXPIRED' | 'CANCELLED';
  outcome_price: string | null;
  outcome_date: string | null;
  return_pct: number | null;
  created_at: string;
  expires_at: string;
}

export interface TrackRecord {
  total_calls: number;
  open_calls: number;
  target_hit: number;
  sl_hit: number;
  expired: number;
  accuracy_pct: number;
  avg_return_pct: number;
  badges: { slug: string; label: string }[];
  recent_calls: StockCall[];
}

export interface SpecializationCategory {
  id: number;
  slug: string;
  name: string;
}

export interface ExpertSearchFilters {
  specializations?: string[];
  language?: string;
  max_rate?: number;
  verified_only?: boolean;
  sort_by?: string;
}

export interface Booking {
  id: number;
  expert_id: number;
  user_id: number;
  expert: string;
  user: string;
  start_time: string;
  end_time?: string;
  booking_type: 'V' | 'C'; // V = Video, C = Chat
  is_booked: boolean;
  is_completed: boolean;
  is_rated?: boolean;
  expert_agora_id?: string;
  user_agora_id?: string;
}

export interface TimeSlot {
  time: string;
  is_available: boolean;
}

export interface AvailabilityData {
  date: string;
  slots: TimeSlot[];
}

export interface Rating {
  id: number;
  user_name: string;
  user_id: number;
  rating: number;
  feedback: string;
  created_at: string;
}

export interface BookingOrder {
  order_id: string;
  amount: number;
  currency: string;
}

export interface ExpertSearchResponse {
  experts: Expert[];
  total_experts: number;
  page: number;
  total_pages: number;
}

export interface AgoraTokenResponse {
  token: string;
  channel: string;
  uid: number;
  app_id: string;
  app_key?: string;
}

// ============ Expert Search ============

/**
 * Search for experts by query string with optional filters
 */
export const handleExpertSearch = async (
  query: string = '',
  page: number = 1,
  filters?: ExpertSearchFilters
): Promise<ExpertSearchResponse> => {
  try {
    const params: Record<string, any> = { q: query, page };
    if (filters?.specializations?.length) {
      params.specializations = filters.specializations.join(',');
    }
    if (filters?.language) params.language = filters.language;
    if (filters?.max_rate) params.max_rate = filters.max_rate;
    if (filters?.verified_only) params.verified_only = 'true';
    if (filters?.sort_by) params.sort_by = filters.sort_by;

    const response = await axiosClient.get(`auth/expert_search/`, { params });

    const data = response.data;
    return {
      experts: data.results?.experts || [],
      total_experts: data.count || 0,
      page: page,
      total_pages: Math.ceil((data.count || 0) / 10),
    };
  } catch (error) {
    console.error('Error searching experts:', error);
    throw error;
  }
};

/**
 * Fetch all specialization categories grouped by type
 */
export const fetchSpecializationCategories = async (): Promise<
  Record<string, SpecializationCategory[]>
> => {
  try {
    const response = await axiosClient.get(`auth/specialization_categories/`);
    return response.data.categories || {};
  } catch (error) {
    console.error('Error fetching specialization categories:', error);
    throw error;
  }
};

/**
 * Fetch current expert's specializations
 */
export const fetchExpertSpecializations = async (): Promise<SpecializationCategory[]> => {
  try {
    const response = await axiosClient.get(`auth/expert_specializations/`);
    return response.data.specializations || [];
  } catch (error) {
    console.error('Error fetching expert specializations:', error);
    throw error;
  }
};

/**
 * Set expert's specializations (list of category IDs)
 */
export const setExpertSpecializations = async (
  categoryIds: number[]
): Promise<{ message: string }> => {
  try {
    const response = await axiosClient.post(`auth/expert_specializations/`, {
      category_ids: categoryIds,
    });
    return response.data;
  } catch (error) {
    console.error('Error setting expert specializations:', error);
    throw error;
  }
};

/**
 * Fetch expert details by user ID
 */
export const fetchExpertDetails = async (expertId: number): Promise<Expert> => {
  try {
    const response = await axiosClient.get(`auth/user_details/`, {
      params: { id: expertId },
    });
    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 401) {
      const response = await axiosClient.get(`auth/user_details/`, {
        params: { id: expertId },
        headers: { Authorization: '' },
      });
      return response.data;
    }
    console.error('Error fetching expert details:', error);
    throw error;
  }
};

// ============ Bookings ============

/**
 * Fetch all bookings for an expert
 */
export const fetchAllBookings = async (
  expertId: number,
  startDate?: string,
  endDate?: string
): Promise<Booking[]> => {
  try {
    const params: Record<string, any> = { expert_id: expertId };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await axiosClient.get(`bookings/expert_booking/`, { params });
    return response.data.bookings || response.data;
  } catch (error) {
    console.error('Error fetching bookings:', error);
    throw error;
  }
};

/**
 * Fetch user's bookings
 */
export const fetchUserBookings = async (startDate?: string): Promise<Booking[]> => {
  try {
    const params: Record<string, any> = {};
    if (startDate) params.st = startDate;

    const response = await axiosClient.get(`bookings/expert_booking/`, { params });
    return response.data.bookings || response.data;
  } catch (error) {
    console.error('Error fetching user bookings:', error);
    throw error;
  }
};

/**
 * Create a new booking with an expert
 */
export const createExpertBooking = async (
  expertId: number,
  data: {
    start_time: string;
    end_time?: string;
    booking_type: 'V' | 'C';
    razorpay_res?: any;
  }
): Promise<Booking> => {
  try {
    const response = await axiosClient.post(
      `bookings/expert_booking/`,
      data,
      { params: { expert_id: expertId } }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating booking:', error);
    throw error;
  }
};

/**
 * Update an existing booking
 */
export const updateExpertBooking = async (
  bookingId: number,
  data: {
    start_time?: string;
    end_time?: string;
    booking_type?: 'V' | 'C';
  }
): Promise<Booking> => {
  try {
    const response = await axiosClient.put(
      `bookings/expert_booking/`,
      data,
      { params: { id: bookingId } }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating booking:', error);
    throw error;
  }
};

/**
 * Accept a booking (for experts)
 */
export const acceptBooking = async (bookingId: number): Promise<Booking> => {
  try {
    const response = await axiosClient.patch(
      `bookings/expert_booking/`,
      { is_booked: true },
      { params: { id: bookingId } }
    );
    return response.data;
  } catch (error) {
    console.error('Error accepting booking:', error);
    throw error;
  }
};

/**
 * Cancel a booking
 */
export const cancelBooking = async (bookingId: number): Promise<void> => {
  try {
    await axiosClient.delete(`bookings/expert_booking/`, {
      params: { id: bookingId },
    });
  } catch (error) {
    console.error('Error canceling booking:', error);
    throw error;
  }
};

// ============ Availability ============

/**
 * Fetch expert's availability for a specific date
 */
export const fetchExpertAvailability = async (
  expertId: number,
  date: string
): Promise<AvailabilityData> => {
  try {
    const response = await axiosClient.get(`bookings/expert_availability/`, {
      params: { id: expertId, date },
    });
    const data = response.data;
    // Backend returns {all_slots: string[], available_slots: string[]}
    // Transform to frontend format {date, slots: TimeSlot[]}
    const allSlots: string[] = data.all_slots || [];
    const availableSlots = new Set(data.available_slots || []);
    const slots: TimeSlot[] = allSlots.map((time: string) => ({
      time,
      is_available: availableSlots.has(time),
    }));
    return { date, slots };
  } catch (error) {
    console.error('Error fetching availability:', error);
    throw error;
  }
};

/**
 * Update expert's availability
 */
export const updateExpertAvailability = async (
  payload: {
    date: string;
    slots: TimeSlot[];
  }
): Promise<AvailabilityData> => {
  try {
    const response = await axiosClient.post(`bookings/expert_availability/`, payload);
    return response.data;
  } catch (error) {
    console.error('Error updating availability:', error);
    throw error;
  }
};

// ============ Ratings ============

/**
 * Fetch ratings for an expert
 */
export const fetchExpertRatings = async (expertId: number): Promise<Rating[]> => {
  try {
    const response = await axiosClient.get(`bookings/expert_rating_feedback/`, {
      params: { id: expertId },
    });
    return response.data.ratings || response.data;
  } catch (error: any) {
    if (error?.response?.status === 401) {
      const response = await axiosClient.get(`bookings/expert_rating_feedback/`, {
        params: { id: expertId },
        headers: { Authorization: '' },
      });
      return response.data.ratings || response.data;
    }
    console.error('Error fetching ratings:', error);
    throw error;
  }
};

/**
 * Submit a rating for an expert
 */
export const submitExpertRating = async (data: {
  id: number; // expert_id
  rating: number;
  feedback: string;
  booking_id: number;
}): Promise<Rating> => {
  try {
    const response = await axiosClient.post(`bookings/expert_rating_feedback/`, data);
    return response.data;
  } catch (error) {
    console.error('Error submitting rating:', error);
    throw error;
  }
};

// ============ Payment ============

export interface BookingCheckoutPreview {
  expert_name: string;
  booking_type: string;
  session_rate: string;
  discount_pct: number;
  discount_amount: string;
  discounted_rate: string;
  platform_fee: string;
  tax: string;
  total_amount: string;
  wallet_balance: string;
  wallet_deduction: string;
  razorpay_amount: string;
  payment_method: 'wallet_only' | 'wallet_and_razorpay' | 'razorpay_only';
}

export const bookingCheckoutPreview = async (
  expertId: number,
  bookingType: 'V' | 'C'
): Promise<BookingCheckoutPreview> => {
  try {
    const response = await axiosClient.post(`bookings/booking_checkout_preview/`, {
      expert_id: expertId,
      booking_type: bookingType,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching booking checkout preview:', error);
    throw error;
  }
};

/**
 * Create a booking order for payment (unified: wallet-only, hybrid, or razorpay-only)
 */
export const createBookingOrder = async (
  expertId: number,
  bookingType: 'V' | 'C',
  startTime?: string,
  endTime?: string,
  useWallet: boolean = true,
): Promise<any> => {
  try {
    const response = await axiosClient.post(`bookings/booking_order/`, {
      expert_id: expertId,
      booking_type: bookingType,
      start_time: startTime,
      end_time: endTime,
      use_wallet: useWallet,
    });
    return response.data;
  } catch (error) {
    console.error('Error creating booking order:', error);
    throw error;
  }
};

// ============ Wallet ============

export interface WalletTransaction {
  id: number;
  transaction_type: string;
  amount: string;
  status: string;
  description: string;
  created_at: string;
}

export interface WalletInfo {
  wallet_balance: string;
  subscription_tier: string;
  subscription_expires: string | null;
  subscription_tenure: string;
  subscription_price_paid: string;
  auto_renew: boolean;
  copilot_queries_used: number;
  copilot_queries_reset_at: string | null;
  transactions: WalletTransaction[];
}

export type SubscriptionTenure = 'monthly' | 'half_yearly' | 'yearly';

export interface TenurePricingInfo {
  tenure: SubscriptionTenure;
  tenure_label: string;
  total_price: number;
  days: number;
  monthly_equivalent: number;
  discount_pct: number;
}

export interface SubscriptionPricingResponse {
  pricing: Record<string, TenurePricingInfo[]>;
}

export interface CheckoutPreview {
  plan: string;
  tenure: string;
  plan_price: string;
  proration_credit: string;
  effective_price: string;
  wallet_balance: string;
  wallet_deduction: string;
  razorpay_amount: string;
  payment_method: 'wallet_only' | 'wallet_and_razorpay' | 'razorpay_only';
  days: number;
}

export interface WalletDepositOrder {
  order_id: string;
  amount: number;
  currency: string;
}

export const fetchRazorpayKeyId = async (): Promise<string> => {
  try {
    const response = await axiosClient.get(`bookings/razorpay_key/`);
    return response.data.key_id;
  } catch (error) {
    console.error('Error fetching Razorpay key:', error);
    throw error;
  }
};

export const fetchWalletInfo = async (): Promise<WalletInfo> => {
  try {
    const response = await axiosClient.get(`bookings/wallet_info/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching wallet info:', error);
    throw error;
  }
};

export const createWalletDepositOrder = async (amount: number): Promise<WalletDepositOrder> => {
  try {
    const response = await axiosClient.post(`bookings/wallet_deposit_order/`, { amount });
    return response.data;
  } catch (error) {
    console.error('Error creating wallet deposit order:', error);
    throw error;
  }
};

export const verifyWalletDeposit = async (paymentData: {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}): Promise<{ message: string; wallet_balance: string }> => {
  try {
    const response = await axiosClient.post(`bookings/wallet_deposit_verify/`, paymentData);
    return response.data;
  } catch (error) {
    console.error('Error verifying wallet deposit:', error);
    throw error;
  }
};

// ============ Subscription ============

export const fetchSubscriptionPricing = async (): Promise<SubscriptionPricingResponse> => {
  try {
    const response = await axiosClient.get(`bookings/subscription_pricing/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching subscription pricing:', error);
    throw error;
  }
};

export const subscriptionCheckoutPreview = async (
  plan: string,
  tenure: SubscriptionTenure,
): Promise<CheckoutPreview> => {
  try {
    const response = await axiosClient.post(`bookings/subscription_checkout_preview/`, {
      plan,
      tenure,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching checkout preview:', error);
    throw error;
  }
};

export const createSubscriptionOrder = async (
  plan: 'pro' | 'premium',
  tenure: SubscriptionTenure = 'monthly',
  useWallet: boolean = false,
): Promise<any> => {
  try {
    const response = await axiosClient.post(`bookings/subscription_order/`, {
      plan,
      tenure,
      use_wallet: useWallet,
    });
    return response.data;
  } catch (error) {
    console.error('Error creating subscription order:', error);
    throw error;
  }
};

export const verifySubscription = async (paymentData: {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  plan: string;
  tenure?: string;
}): Promise<{
  message: string;
  subscription_tier: string;
  subscription_tenure: string;
  subscription_expires: string;
  wallet_balance: string;
}> => {
  try {
    const response = await axiosClient.post(`bookings/subscription_verify/`, paymentData);
    return response.data;
  } catch (error) {
    console.error('Error verifying subscription:', error);
    throw error;
  }
};

export const toggleAutoRenew = async (
  autoRenew: boolean,
): Promise<{ auto_renew: boolean }> => {
  try {
    const response = await axiosClient.patch(`bookings/subscription_auto_renew/`, {
      auto_renew: autoRenew,
    });
    return response.data;
  } catch (error) {
    console.error('Error toggling auto-renew:', error);
    throw error;
  }
};

// ============ Wallet Booking ============

export const createWalletBooking = async (data: {
  expert_id: number;
  start_time: string;
  end_time: string;
  booking_type: 'V' | 'C';
}): Promise<{ message: string; wallet_balance: string }> => {
  try {
    const response = await axiosClient.post(`bookings/wallet_booking/`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating wallet booking:', error);
    throw error;
  }
};

// ============ Chat History ============

export interface ChatMessageRecord {
  id: number;
  sender_profile_id: number;
  message_text: string;
  agora_message_id: string | null;
  created_at: string;
}

export const saveChatMessage = async (
  bookingId: number,
  messageText: string,
  agoraMessageId?: string
): Promise<{ id: number; created_at: string }> => {
  try {
    const response = await axiosClient.post(`bookings/chat_message/`, {
      booking_id: bookingId,
      message_text: messageText,
      agora_message_id: agoraMessageId || null,
    });
    return response.data;
  } catch (error) {
    console.error('Error saving chat message:', error);
    throw error;
  }
};

export interface ChatHistoryResponse {
  your_profile_id: number;
  messages: ChatMessageRecord[];
}

export const fetchChatHistory = async (
  bookingId: number
): Promise<ChatHistoryResponse> => {
  try {
    const response = await axiosClient.get(`bookings/chat_history/`, {
      params: { booking_id: bookingId },
    });
    return {
      your_profile_id: response.data.your_profile_id,
      messages: response.data.messages || [],
    };
  } catch (error) {
    console.error('Error fetching chat history:', error);
    throw error;
  }
};

// ============ Agora Tokens ============

/**
 * Fetch Agora token for video call
 */
export const fetchAgoraToken = async (bookingId: number): Promise<AgoraTokenResponse> => {
  try {
    const response = await axiosClient.get(`bookings/agora_token/`, {
      params: { id: bookingId },
    });
    const data = response.data;
    return {
      token: data.token,
      channel: data.channel_name,
      uid: parseInt(data.uid) || 0,
      app_id: data.app_id,
    };
  } catch (error) {
    console.error('Error fetching Agora token:', error);
    throw error;
  }
};

/**
 * Fetch Agora chat token
 */
export const fetchAgoraChatToken = async (): Promise<AgoraTokenResponse> => {
  try {
    const response = await axiosClient.get(`bookings/agora_chat_token/`);
    const data = response.data;
    return {
      token: data.token,
      channel: '',
      uid: data.uid,
      app_id: '',
      app_key: data.app_key,
    };
  } catch (error) {
    console.error('Error fetching Agora chat token:', error);
    throw error;
  }
};

// ============ Expert Stock Calls ============

export const createStockCall = async (data: {
  ticker_symbol: string;
  company_name: string;
  exchange?: string;
  call_type: 'BUY' | 'SELL';
  entry_price: number;
  target_price: number;
  stop_loss_price: number;
  time_horizon_days?: number;
  rationale?: string;
}): Promise<{ id: number; status: string; message: string }> => {
  try {
    const response = await axiosClient.post(`calls/create/`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating stock call:', error);
    throw error;
  }
};

export const fetchExpertCalls = async (
  userId: number,
  status?: string,
  page: number = 1,
): Promise<{ total: number; page: number; calls: StockCall[] }> => {
  try {
    const params: Record<string, any> = { user_id: userId, page };
    if (status) params.status = status;
    const response = await axiosClient.get(`calls/expert_calls/`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching expert calls:', error);
    throw error;
  }
};

export const fetchMyCalls = async (
  status?: string,
  page: number = 1,
): Promise<{ total: number; page: number; calls: StockCall[] }> => {
  try {
    const params: Record<string, any> = { page };
    if (status) params.status = status;
    const response = await axiosClient.get(`calls/my_calls/`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching my calls:', error);
    throw error;
  }
};

export const fetchTrackRecord = async (userId: number): Promise<TrackRecord> => {
  try {
    const response = await axiosClient.get(`calls/track_record/`, {
      params: { user_id: userId },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching track record:', error);
    throw error;
  }
};

export const cancelStockCall = async (callId: number): Promise<{ message: string }> => {
  try {
    const response = await axiosClient.delete(`calls/cancel/${callId}/`);
    return response.data;
  } catch (error) {
    console.error('Error cancelling stock call:', error);
    throw error;
  }
};
